import React, { useState } from 'react';
import { Typography, TextField, Button, Modal, Spinner } from '@scale/scaleui';
import { useNormalizedResumeMetadataTerms } from './data/useNormalizedResumeMetadataTerms';
import { toast } from 'react-toastify';

interface NormalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NormalizeModal: React.FC<NormalizeModalProps> = ({ isOpen, onClose }) => {
  const [rawInput, setRawInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [termsToNormalize, setTermsToNormalize] = useState<string[]>([]);
  const { data: normalizedTerms, isLoading } = useNormalizedResumeMetadataTerms(termsToNormalize);

  const handleSubmit = () => {
    if (!rawInput.trim()) {
      setInputError('Please enter terms to normalize');
      return;
    }

    const values = rawInput
      .split(',')
      .map((v: string) => v.trim())
      .filter((v: string) => v !== '');
    if (values.length === 0) {
      setInputError('Please enter terms to normalize');
      return;
    }
    if (values.length > 200) {
      setInputError('Please enter less than 200 terms to normalize');
      return;
    }
    setTermsToNormalize(values);
    setInputError(null);
  };

  const handleClose = () => {
    onClose();
    setRawInput('');
    setTermsToNormalize([]);
    setInputError(null);
  };

  const handleCopyToClipboard = () => {
    if (normalizedTerms) {
      const csvString = normalizedTerms.join(',');
      navigator.clipboard
        .writeText(csvString)
        .then(() => {
          toast(`Copied to clipboard`, {
            type: 'success',
            autoClose: 2000,
            position: 'top-right',
          });
          return;
        })
        .catch(e => {
          toast(`Failed to copy: ${e}`, {
            type: 'error',
            autoClose: 2000,
            position: 'top-right',
          });
        });
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} title="Normalize Values">
      <div className="flex flex-col gap-4">
        <Typography.Paragraph2>
          Enter comma-separated terms to normalize: (200 terms max)
        </Typography.Paragraph2>
        <TextField
          value={rawInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRawInput(e.target.value);
            setInputError(inputError);
          }}
          placeholder="e.g. term1, term2, term3"
          error={!!inputError}
        />
        {inputError && (
          <Typography.Paragraph2 className="text-danger-500">{inputError}</Typography.Paragraph2>
        )}
        <div className="flex flex-row justify-end gap-2">
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </div>
        {isLoading && (
          <div className="flex items-center justify-center gap-2 py-4">
            <Spinner size="sm" />
            <Typography.Paragraph2>Normalizing terms...</Typography.Paragraph2>
          </div>
        )}
        {normalizedTerms && normalizedTerms.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3>Normalized Terms:</h3>
              <Button variant="secondary" onClick={handleCopyToClipboard}>
                Copy to clipboard as CSV
              </Button>
            </div>
            <ul>
              {normalizedTerms.map((term: string, index: number) => (
                <li key={index}>{term}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NormalizeModal;
